import React, { useState, useEffect, ReactNode, TouchEvent, useRef } from "react";
import useMobile from "@/app/hooks/use-mobile";
import { UEListData } from "@/app/interface/homepage";
// import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import LoadingIcon from "@/app/assets/icons/loading";
import { useRouter } from "next/navigation";
import { Drawer } from "vaul";
import { formatIndianCurrency } from "@/app/utils/helper";
import Info from "@/app/assets/icons/info";
import { ISINPrices } from "@/app/interface/assetpage";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { User } from "@/app/interface/user";
import { StaggeringData } from "@/app/alts/unlisted-shares/[...id]/page-client";
import Image from "next/image";
import moment from "moment";
import { DirectPaths } from "@/config/routes";
import { BLOCKED_SHARE_STATES, ProductVisibility } from "@/app/utils/product-visibility";

interface SwipeableDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
}

const SwipeableDrawer: React.FC<SwipeableDrawerProps> = ({ isOpen, onClose, children }) => {
    const [startY, setStartY] = useState<number | null>(null);
    const [currentY, setCurrentY] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const bodyRef = useRef<HTMLBodyElement | null>(null);

    useEffect(() => {
        bodyRef.current = document.querySelector("body");

        if (isOpen) {
            bodyRef.current?.classList.add("overflow-hidden");
        } else {
            bodyRef.current?.classList.remove("overflow-hidden");
        }

        return () => {
            bodyRef.current?.classList.remove("overflow-hidden");
        };
    }, [isOpen]);

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
        setStartY(e.touches[0].clientY);
        setIsDragging(true);
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
        if (!startY) return;
        const currentTouch: number = e.touches[0].clientY;
        const diff: number = currentTouch - startY;
        if (diff > 0) {
            setCurrentY(diff);
        }
    };

    const handleTouchEnd = (): void => {
        setIsDragging(false);
        if (currentY > 100) {
            onClose();
        }
        setCurrentY(0);
        setStartY(null);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50">
            <div
                className="fixed inset-0 bg-gray-700 bg-opacity-50 transition-opacity"
                onClick={onClose}
            />
            <div
                className={`fixed bottom-0 left-0 right-0 transform rounded-t-3xl bg-gray-200 shadow-lg transition-transform duration-300 ease-out ${
                    isOpen && !isDragging ? "translate-y-0 delay-150" : "translate-y-full"
                }`}
                style={{
                    transform: `translateY(${isDragging ? currentY : 0}px)`,
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                <div
                    className="w-full cursor-grab active:cursor-grabbing"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="mx-auto my-4 h-1 w-16 rounded-full bg-gray-300" />
                </div>
                <div className="px-4 pb-8">{children}</div>
            </div>
        </div>
    );
};

interface ScrollableData {
    isAuthenticated: boolean;
    txnRef: React.RefObject<HTMLButtonElement>;
    stockDetails: UEListData;
    units: number;
    setUnits: (value: number) => void;
    isPurchaseStart: boolean;
    startTransaction: () => void;
    userData: User | null;
    unitsPrice: ISINPrices | undefined;
    isAllDataFetched: boolean;
    staggeringPrices: StaggeringData[];
    setopenStaggeringTable: (value: boolean) => void;
    getBtnTxt: () => string;
    decreaseUnits: () => void;
    handleUnitsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleIncreaseUnits: () => void;
    setisMobileWidgetOpen: (value: boolean) => void;
}

const ScrollableButton: React.FC<ScrollableData> = ({
    isAuthenticated,
    txnRef,
    stockDetails,
    units,
    setUnits,
    startTransaction,
    userData,
    unitsPrice,
    isAllDataFetched,
    staggeringPrices,
    setopenStaggeringTable,
    isPurchaseStart,
    getBtnTxt,
    decreaseUnits,
    handleUnitsChange,
    handleIncreaseUnits,
    setisMobileWidgetOpen,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isWidgetOpen, setisWidgetOpen] = useState<boolean>(false);
    const {isMobile} = useMobile();

    const router = useRouter();
    if (!isMobile) return null;

    const handleLogin = () => {
        router.push(`/login?redirect=${DirectPaths.UNLISTED_SHARES}/${stockDetails?.product}`);
    };

    const WidgetClose = () => {
        setisMobileWidgetOpen(false);
        setisWidgetOpen(false);
        if (units < (stockDetails?.minUnits ?? 0)) {
            setUnits(stockDetails?.minUnits ?? 0);
        }
    };

    return (
        <>
            {!isWidgetOpen && (
                <div className="fixed bottom-0 left-0 z-40 w-full">
                    <div className="w-full bg-white px-4 py-3 shadow-md">
                        <div className="container mx-auto flex items-center justify-center">
                            <RegularButton
                                ref={txnRef}
                                noIcon={
                                    userData ? (!isAllDataFetched ? false : !isPurchaseStart) : true
                                }
                                icon={
                                    userData ? (
                                        isPurchaseStart || !isAllDataFetched ? (
                                            <LoadingIcon className="animate-spin" />
                                        ) : (
                                            <></>
                                        )
                                    ) : (
                                        <></>
                                    )
                                }
                                label={
                                    !userData
                                        ? getBtnTxt()
                                        : isPurchaseStart
                                          ? ""
                                          : isAllDataFetched
                                            ? getBtnTxt()
                                            : ""
                                }
                                showRightIcon={false}
                                variant={ButtonVariant.PrimaryDefault}
                                size={ButtonSize.Medium}
                                className="min-w-full max-w-full flex-1"
                                onClick={() => {
                                    if (userData && !isWidgetOpen) {
                                        setisWidgetOpen(true);
                                        setisMobileWidgetOpen(true);
                                    } else if (isWidgetOpen) {
                                        WidgetClose();
                                        startTransaction();
                                    } else {
                                        setIsDrawerOpen(true);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {isWidgetOpen && userData ? (
                <Drawer.Root
                    open={isWidgetOpen}
                    onClose={WidgetClose}
                    dismissible={!stockDetails?.isProductAvailable ? true : false}
                >
                    <Drawer.Portal>
                        <Drawer.Title>
                            <VisuallyHidden>Purchase Widget</VisuallyHidden>
                        </Drawer.Title>
                        <Drawer.Overlay className="fixed inset-0 bg-black/80" />
                        <Drawer.Content
                            onClick={(e) => e.stopPropagation()}
                            className="fixed bottom-0 left-0 right-0 mb-7 mt-24 flex h-auto flex-col rounded-t-[10px] bg-white outline-none"
                        >
                            <div>
                                {/* <div aria-hidden className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-gray-300 mb-8" /> */}
                                {/* {false && (
                                    <div className="flex items-center justify-between gap-1 rounded-t-xl bg-brand-primary-100 px-s9 py-s7">
                                        <div>
                                            <p className="text-nowrap text-xs font-medium leading-[140%] tracking-[0.14px]">
                                                Want to buy this in a smaller quantity?
                                            </p>
                                            <p className="text-xs font-regular text-neutral-500">
                                                Also available for orders of just 25 units
                                            </p>
                                        </div>
                                        <ChevronRight />
                                    </div>
                                )} */}
                                {!stockDetails?.isProductAvailable && (
                                    <div className="absolute left-0 top-0 z-10 max-h-[740px] w-full rounded-xl bg-white px-2 opacity-95">
                                        <div className="mt-[50px] flex flex-col items-center justify-between px-6">
                                            <Image
                                                src={stockDetails?.company?.logoUrl}
                                                className="bg-white"
                                                alt="logo"
                                                width={162}
                                                height={162}
                                            />
                                            <h3 className="pt-2 text-center text-xl font-semibold text-gray-900">
                                                {stockDetails?.company?.displayName}
                                            </h3>
                                            <p className="mb-2 mt-2 px-4 py-2 text-center text-sm font-medium text-black opacity-100">
                                                Sold out. Tap Notify for updates!
                                            </p>
                                            <RegularButton
                                                noIcon={true}
                                                label="Notify Me"
                                                showRightIcon={false}
                                                variant={ButtonVariant.PrimaryDefault}
                                                size={ButtonSize.Medium}
                                                onClick={startTransaction}
                                                className="mt-auto min-w-full max-w-full"
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="p-s9">
                                    <h3 className="flex items-center justify-between text-base font-semibold text-gray-900">
                                        {stockDetails?.company?.displayName}{" "}
                                        <span>
                                            <X onClick={WidgetClose} />
                                        </span>
                                    </h3>
                                </div>
                                <div className="mx-s9 border-b border-dashed border-neutralOpacityDark-20"></div>
                                <div className="mx-s9 my-s7">
                                    <div className="flex h-[38px] items-center justify-between">
                                        <span className="text-sm font-regular text-neutral-500">
                                            Price per unit
                                        </span>
                                        <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                            {formatIndianCurrency(unitsPrice?.unitPrice ?? 0)}
                                        </span>
                                    </div>

                                    {stockDetails?.settlementDays?.isShow && (
                                        <div className="flex h-[38px] items-center justify-between">
                                            <div className="flex items-center gap-1">
                                                <span className="text-sm text-gray-600">
                                                    Settlement period
                                                </span>
                                                <div className="group relative">
                                                    <Info
                                                        width="16"
                                                        height="16"
                                                        className="text-neutral-300"
                                                    />
                                                    <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                        For NEFT Payment mode,the exact settlement
                                                        date may vary.
                                                    </div>
                                                </div>
                                            </div>
                                            <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                                {stockDetails?.estimatedSettlementDate
                                                    ? moment(
                                                          stockDetails?.estimatedSettlementDate,
                                                          "MM/DD/YYYY",
                                                      ).format("DD MMM YYYY")
                                                    : `T + ${stockDetails?.settlementDays?.value} days`}
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex h-[38px] items-center justify-between">
                                        <span className="text-sm text-gray-600">Min. units</span>
                                        <span className="text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-900">
                                            {stockDetails?.minUnits}
                                        </span>
                                    </div>

                                    <div
                                        className={`flex h-[38px] items-center justify-between ${staggeringPrices ? "" : "pb-4"}`}
                                    >
                                        <div className="flex w-full flex-col">
                                            <div className="flex w-full items-center justify-between">
                                                <span
                                                    className={`${String(units).length < 5 ? "" : "flex-1"} text-sm text-gray-600`}
                                                >
                                                    No. of units to buy
                                                </span>

                                                <div className="flex items-center">
                                                    <button
                                                        className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                        onClick={decreaseUnits}
                                                        disabled={
                                                            units <= (stockDetails?.minUnits || 0)
                                                        }
                                                    >
                                                        -
                                                    </button>
                                                    <input
                                                        className={`${String(units).length < 5 ? "w-12" : String(units).length < 7 ? "mx-3 w-14" : "mx-3 w-16"} text-center text-md font-regular leading-[160%] tracking-[0.14px] text-neutral-900 focus:outline-none`}
                                                        type="text"
                                                        value={units}
                                                        onChange={handleUnitsChange}
                                                    />
                                                    <button
                                                        className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-black hover:bg-neutral-200"
                                                        onClick={handleIncreaseUnits}
                                                        disabled={
                                                            units >=
                                                            (stockDetails?.maxUnits ??
                                                                Number.MAX_SAFE_INTEGER)
                                                        }
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                {units < (stockDetails?.minUnits ?? 0) && (
                                                    <p className="mb-2 text-center text-sm text-brand-primary-500">
                                                        Minimum units should be{" "}
                                                        {stockDetails?.minUnits}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {staggeringPrices.length > 0 && (
                                        <div
                                            className="flex h-[25px] items-center justify-between pb-6 pt-3"
                                            onClick={() => setopenStaggeringTable(true)}
                                        >
                                            <span className="cursor-pointer text-sm font-regular text-brand-primary-500 underline underline-offset-2">
                                                Grab this opportunity at{" "}
                                                {formatIndianCurrency(
                                                    staggeringPrices[staggeringPrices.length - 1]
                                                        ?.price,
                                                )}
                                            </span>
                                        </div>
                                    )}
                                    <div className="border-b border-dashed border-neutralOpacityDark-20"></div>
                                    <div className="pt-s9">
                                        {stockDetails?.isProductAvailable && (
                                            <div className="flex h-[38px] items-center justify-between">
                                                <span className="text-sm text-neutral-500">
                                                    Final amount
                                                </span>
                                                <span className="text-lg font-semibold leading-[164%] tracking-[-0.18px]">
                                                    {units >= (stockDetails?.minUnits ?? 0)
                                                        ? formatIndianCurrency(
                                                              unitsPrice?.amount ?? 0,
                                                          )
                                                        : "₹0"}
                                                </span>
                                            </div>
                                        )}
                                        {(unitsPrice?.benefits?.total?.discountAmount ?? 0) > 0 &&
                                        stockDetails?.isProductAvailable ? (
                                            <>
                                                <div className="flex h-[38px] items-center justify-between">
                                                    <span className="text-sm text-neutral-500">
                                                        Offer benefit(0.25%)
                                                    </span>
                                                    <span className="text-sm font-regular leading-[160%] tracking-[0.14px]">
                                                        {units >= (stockDetails?.minUnits ?? 0)
                                                            ? formatIndianCurrency(
                                                                  unitsPrice?.benefits?.total
                                                                      ?.discountAmount ?? 0,
                                                              )
                                                            : "₹0"}
                                                    </span>
                                                </div>
                                                {unitsPrice?.benefits?.referralBenefits
                                                    ?.cashBackToReferralOnFirstTxn &&
                                                unitsPrice?.benefits?.referralBenefits
                                                    ?.cashBackToReferralOnFirstTxn > 0 ? (
                                                    <div className="flex h-[38px] items-center justify-between">
                                                        <span className="flex items-center gap-1 text-sm text-neutral-500">
                                                            Cash back
                                                            <div className="group relative">
                                                                <Info
                                                                    width="16"
                                                                    height="16"
                                                                    className="text-neutral-300"
                                                                />
                                                                <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-xs text-white group-hover:block">
                                                                    You will receive{" "}
                                                                    <b>
                                                                        {formatIndianCurrency(
                                                                            unitsPrice?.benefits
                                                                                ?.referralBenefits
                                                                                ?.cashBackToReferralOnFirstTxn,
                                                                        )}
                                                                        /- Amazon
                                                                        <br />
                                                                        voucher
                                                                    </b>{" "}
                                                                    with in 30 days of order
                                                                    <br />
                                                                    confirmation
                                                                </div>
                                                            </div>
                                                        </span>
                                                        <span className="text-sm font-regular leading-[160%] tracking-[0.14px]">
                                                            {formatIndianCurrency(
                                                                unitsPrice?.benefits
                                                                    ?.referralBenefits
                                                                    ?.cashBackToReferralOnFirstTxn,
                                                            )}
                                                        </span>
                                                    </div>
                                                ) : null}
                                                <div className="flex h-[38px] items-center justify-between">
                                                    <span className="text-sm text-neutral-500">
                                                        Total Investment amount
                                                    </span>
                                                    <span className="text-lg font-semibold leading-[160%] tracking-[0.14px]">
                                                        {units >= (stockDetails?.minUnits ?? 0)
                                                            ? formatIndianCurrency(
                                                                  unitsPrice?.benefits?.total
                                                                      ?.withBenefitAmount ?? 0,
                                                              )
                                                            : "₹0"}
                                                    </span>
                                                </div>
                                            </>
                                        ) : null}
                                    </div>
                                    {isAuthenticated &&
                                    stockDetails?.shareState &&
                                    BLOCKED_SHARE_STATES.has(
                                        stockDetails.shareState.toLowerCase().trim(),
                                    ) ? (
                                        <div className="px-s9 pb-s6 pt-s6 md:pt-0">
                                            <div className="rounded-lg border border-brand-primary-200 bg-[#feefea] px-3 py-3 text-center text-sm font-medium leading-[160%] tracking-[1%] text-brand-primary-600">
                                                {`The company is in ${ProductVisibility[stockDetails.shareState.toLowerCase().trim() as keyof typeof ProductVisibility]} stage; transactions are not allowed!`}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="py-4">
                                            {stockDetails?.isProductAvailable && (
                                                <RegularButton
                                                    noIcon={!isPurchaseStart}
                                                    icon={
                                                        isPurchaseStart ? (
                                                            <LoadingIcon className="animate-spin" />
                                                        ) : (
                                                            <></>
                                                        )
                                                    }
                                                    label={isPurchaseStart ? "" : getBtnTxt()}
                                                    showRightIcon={false}
                                                    disabled={
                                                        isPurchaseStart
                                                            ? true
                                                            : units < (stockDetails?.minUnits ?? 0)
                                                    }
                                                    variant={ButtonVariant.PrimaryDefault}
                                                    size={ButtonSize.Medium}
                                                    className="min-w-full max-w-full flex-1"
                                                    onClick={startTransaction}
                                                />
                                            )}
                                            {stockDetails?.ccpsDisclaimer &&
                                            stockDetails?.isProductAvailable ? (
                                                <p
                                                    onClick={() =>
                                                        window.open(
                                                            stockDetails?.ccpsDisclaimer?.link,
                                                            "",
                                                        )
                                                    }
                                                    className="cursor-pointer px-s9 pt-s6 text-center text-sm font-regular leading-[160%] tracking-[0.14px] text-neutral-300 underline"
                                                >
                                                    {stockDetails?.ccpsDisclaimer?.title}
                                                </p>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            ) : (
                <SwipeableDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                    {!isAuthenticated &&
                    stockDetails?.shareState &&
                    BLOCKED_SHARE_STATES.has(stockDetails.shareState.toLowerCase().trim()) ? (
                        <div>
                            <div className="px-3 py-3 text-center text-sm text-brand-primary-600 font-medium leading-[160%] tracking-[1%]">
                                {`The company is in ${ProductVisibility[stockDetails.shareState.toLowerCase().trim() as keyof typeof ProductVisibility]} stage; transactions are not allowed!`}
                            </div>
                            <RegularButton
                                noIcon={true}
                                label={"Explore other shares"  }
                                showRightIcon={false}
                                variant={ButtonVariant.PrimaryDefault}
                                size={ButtonSize.Medium}
                                className="min-w-full max-w-full flex-1 mt-2"
                                onClick={() => router.push(DirectPaths.UNLISTED_SHARES)}
                            />
                        </div>
                    ) : (
                        <div className="space-y-6 text-center">
                            <h2 className="mt-[75px] text-center text-lg font-medium text-black">
                                Login and get full price <br /> about this unlisted stock
                            </h2>
                            <button
                                className="mb-8 mt-4 w-full rounded-full bg-white px-2 py-3 text-center font-medium text-black"
                                onClick={handleLogin}
                            >
                                Login
                            </button>
                        </div>
                    )}
                </SwipeableDrawer>
            )}
        </>
    );
};

export default ScrollableButton;